import { QueryClient, defaultShouldDehydrateQuery } from '@tanstack/react-query'
import { isOnServer } from '@shared/constants/util'
import { minutesToMilliseconds } from 'date-fns'
import { type RequestCookies, type RequestHeaders } from '@shared/request_type'

export type ServerContext = {
  cookies: RequestCookies
  headers: RequestHeaders
  getAccessToken: () => Promise<string>
}
type QueryMetaExtra = {
  observerThreshold?: number
  marvUpdateBehavior?: 'append' | 'replace'
}

type NoServerContext = { [K in keyof ServerContext]?: never }

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: (ServerContext | NoServerContext) & QueryMetaExtra
    mutationMeta: ServerContext | NoServerContext
  }
}
// singleton instance of the queryClient used on member devices
// most of the time this will be provided by the <QueryClientProvider> in _app.tsx
// but its here for convenience when used by getQueryClient
let _queryClient: QueryClient | undefined

export const createQueryClient = (
  serverContext?: ServerContext,
  { isAppRouter = false }: { isAppRouter?: boolean } = {},
) =>
  new QueryClient({
    defaultOptions: {
      // we want to dehydrate pending queries only in the app router
      ...(isAppRouter && {
        dehydrate: {
          // include pending queries in dehydration
          shouldDehydrateQuery: (query) =>
            defaultShouldDehydrateQuery(query) || query.state.status === 'pending',
          // We should not catch Next.js server errors as that's how
          // Next.js detects dynamic pages so we cannot redact them.
          // Next.js also automatically redacts errors for us with better digests.
          shouldRedactErrors: () => false,
        },
      }),
      queries: {
        // by setting serverContext here, we don't need to pass ssrContext
        // around in every method that might need access to the context
        meta: serverContext,
        // we want API calls to adhere to a retry "opt-in" policy by default
        // to prevent unnecessary API calls
        retry: false,
        staleTime: minutesToMilliseconds(30),
      },
      mutations: {
        meta: serverContext,
      },
    },
  })

export const getQueryClient = (serverContext?: ServerContext) => {
  if (isOnServer()) return createQueryClient(serverContext)
  if (!_queryClient) {
    _queryClient = createQueryClient()
  }
  return _queryClient
}
